import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Lock, Eye, EyeOff, Mail } from 'lucide-react';


const Login = () => {
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // Updated Auth0 configuration with your new details
  const AUTH0_CONFIG = {
    domain: "vms-optimus.us.auth0.com",
    clientId: "fcnJvdlwufooitjKCG0OFiw5eXF317Fn",
    audience: "https://vms-optimus.us.auth0.com/api/v2/",
    connection: "Username-Password-Authentication"
  };

  const handleLogin = async () => {
    if (!email || !password) {
      setError("Both email and password fields are required.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const tokenData = new URLSearchParams({
        grant_type: "http://auth0.com/oauth/grant-type/password-realm",
        username: email,
        password: password,
        audience: AUTH0_CONFIG.audience,
        client_id: AUTH0_CONFIG.clientId,
        realm: AUTH0_CONFIG.connection,
        scope: "openid profile email",
      });

      const response = await fetch(`https://${AUTH0_CONFIG.domain}/oauth/token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept": "application/json",
        },
        body: tokenData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error_description || "Login failed");
      }

      // Get user information
      const userResponse = await fetch(`https://${AUTH0_CONFIG.domain}/userinfo`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${data.access_token}`,
        },
      });

      const userData = await userResponse.json();

      if (!userResponse.ok) {
        throw new Error("Failed to fetch user information");
      }

// Debug the userData first
console.log("UserData from Auth0:", userData);
console.log("User email:", userData.email);

try {
  // Get Management API token to fetch user roles AND full profile
  const mgmtTokenResponse = await fetch(`https://vms-optimus.us.auth0.com/oauth/token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      client_id: "ZxowKlLpWMVvNbUuzMKooWx0FA5jhxYg",
      client_secret: "XTH0sFAFWTqpZe8gYn9PJtVU4rg-6hctN7PROhojbsQJ2ZSVy7Nabk5kZSoihlnm",
      audience: "https://vms-optimus.us.auth0.com/api/v2/",
      grant_type: "client_credentials",
      scope: "read:users read:roles read:users_app_metadata"  // read:users_app_metadata
    }),
  });

  const mgmtTokenData = await mgmtTokenResponse.json();


  const fullUserResponse = await fetch(
    `https://vms-optimus.us.auth0.com/api/v2/users/${userData.sub}`,
    {
      headers: { Authorization: `Bearer ${mgmtTokenData.access_token}` },
    }
  );
  const fullUserData = fullUserResponse.ok ? await fullUserResponse.json() : null;

  // Fetch user roles
  const roleResponse = await fetch(`https://vms-optimus.us.auth0.com/api/v2/users/${userData.sub}/roles`, {
    headers: { Authorization: `Bearer ${mgmtTokenData.access_token}` },
  });

  const roles = roleResponse.ok ? await roleResponse.json() : [];

  // Map role ID to role name
  const roleMap = {
    "rol_xUv1JEhGgOWMSTUh": "Admin",
    "rol_sNukGt3kUZddyKeO": "Employee",
    "rol_tn4hgiqrbDtFxEY7": "Concierge" 
  };

  const userRole = roles.length > 0 ? roleMap[roles[0].id] || 'Employee' : 'Employee';


  const auth0User = fullUserData || userData;
  const username = 
    auth0User.user_metadata?.username ||       
    auth0User.name ||
    auth0User.nickname ||
    userData.email.split("@")[0];

  // Store user details
  const userToStore = {
    username: username,  // 
    email: userData.email,
    sub: userData.sub,
    role: userRole
  };

  console.log("Storing user data:", userToStore);
  localStorage.setItem("user", JSON.stringify(userToStore));

} catch (roleError) {
  console.error('Error fetching user role:', roleError);
  
  const fallbackUser = {
    username: userData.name || userData.nickname || userData.email.split("@")[0],
    email: userData.email,
    sub: userData.sub,
    role: 'Employee'
  };
  
  localStorage.setItem("user", JSON.stringify(fallbackUser));
}

localStorage.setItem("access_token", data.access_token);

// Navigate to dashboard
navigate("/invite");



      
    } catch (error) {
      setError(error.message || "Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async () => {
    if (!email || !password || !username) {
      setError("All fields are required.");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }

    if (username.length < 3) {
      setError("Username must be at least 3 characters long.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const signUpData = {
        client_id: AUTH0_CONFIG.clientId,
        email: email,
        password: password,
        connection: AUTH0_CONFIG.connection,
        user_metadata: { username: username },
      };

      const response = await fetch(`https://${AUTH0_CONFIG.domain}/dbconnections/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(signUpData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "User already exists. Please log in.");
      }

      console.log("Sign-Up successful! Username stored:", username);

      // Show success message and switch to login
      setError("Sign-Up successful! Please log in.");
      setIsSignUp(false);

      // Reset form fields after successful signup
      setEmail("");
      setPassword("");
      setUsername("");

    } catch (error) {
      setError(error.message || "Sign-up failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleMode = () => {
    setIsSignUp(!isSignUp);
    setError("");
    setEmail("");
    setPassword("");
    setUsername("");
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      isSignUp ? handleSignUp() : handleLogin();
    }
  };

  return (
    <div className="min-h-screen flex overflow-hidden">
      {/* Left Section - Blue gradient background */}
      <div className="flex-1 relative bg-gradient-to-br from-[#021B79] via-[#22469C] to-teal-600 flex flex-col justify-center items-start p-12">
        {/* Logo - Top Left */}
        <div className="absolute top-8 left-8">
          <img src="/white.svg" alt="Logo" className="w-10 h-10" />
        </div>

        {/* Decorative circles */}
        {/* <div className="absolute bottom-10 left-0 w-96 h-96 border border-white border-opacity-40 rounded-full -translate-x-48 translate-y-48"></div>
        <div className="absolute bottom-6 left-20 w-80 h-80 border border-white border-opacity-20 rounded-full -translate-x-40 translate-y-40"></div> */}

        {/* Main Content */}
        <div className="relative z-10 lg:max-w-md 2xl:max-w-full">
          <h1 className="text-7xl font-light text-white mb-10">Optimus</h1>
          <p className="text-5xl font-light text-white leading-tight text-opacity-90">Visitor Management System</p>
        </div>
      </div>

      {/* Right Section - Login/Signup Form */}
      <div className="flex-1 bg-white flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {isSignUp ? "Create Account" : "Hello!"}
            </h2>
            <p className="text-gray-600">
              {isSignUp ? "Sign up to get started" : "Login to Get Started"}
            </p>
          </div>

          {/* Error/Success Message */}
          {error && (
            <div className={`mb-4 p-3 rounded-lg text-sm ${
              error.includes("successful") 
                ? "bg-green-100 text-green-700 border border-green-200"
                : "bg-red-100 text-red-700 border border-red-200"
            }`}>
              {error}
            </div>
          )}

          <div className="space-y-6">
            {/* Username Field (Only in Sign-Up Mode) */}
            {isSignUp && (
              <div>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Username"
                    className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    disabled={isLoading}
                  />
                </div>
              </div>
            )}

            {/* Email Field */}
            <div>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Email Address"
                  className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Password"
                  className="w-full pl-12 pr-12 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                  disabled={isLoading}
                >
                  {showPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Login/Sign-Up Button */}
            <button
              type="button"
              onClick={isSignUp ? handleSignUp : handleLogin}
              disabled={isLoading}
              className="w-full bg-blue-800 text-white py-4 rounded-xl font-semibold hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  {isSignUp ? "Creating Account..." : "Logging in..."}
                </div>
              ) : (
                isSignUp ? "Create Account" : "Login"
              )}
            </button>
          </div>

          {/* Toggle Between Login & Sign Up */}
          {/* <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              {isSignUp ? "Already have an account? " : "Don't have an account? "}
              <button
                type="button"
                onClick={handleToggleMode}
                disabled={isLoading}
                className="text-blue-800 hover:text-blue-800 font-medium focus:outline-none disabled:opacity-50"
              >
                {isSignUp ? "Login" : "Sign Up"}
              </button>
            </p>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default Login;